/**
 * App Tabs Store
 *
 * Store Zustand untuk mengelola tab-tab halaman yang terbuka di aplikasi.
 * Sistem ini mirip dengan tab browser - setiap halaman dibuka sebagai tab baru.
 *
 * Behavior:
 * - Klik menu sidebar: buka tab baru (jika belum ada) atau switch ke tab existing
 * - Klik tab: switch ke tab tersebut tanpa reload
 * - Tutup tab: hapus tab dan state-nya
 * - State internal halaman (misal: sub-tabs di Hutang Usaha) tersimpan selama tab terbuka
 *
 * Keamanan:
 * - State tabs di-reset saat logout (tidak di-persist ke localStorage)
 */

import { create } from 'zustand';
import type { ReactNode } from 'react';

// ============================================
// TYPES
// ============================================

/**
 * Definisi tab yang tersedia di aplikasi
 * Setiap tab memiliki ID unik, label, path, dan component
 */
export interface AppTabDefinition {
  id: string;
  label: string;
  path: string;
  icon?: ReactNode;
}

/**
 * State tab yang sedang terbuka
 */
export interface AppTabState {
  id: string;
  label: string;
  path: string;
  icon?: ReactNode;
}

/**
 * Store state
 */
interface AppTabsState {
  // Tab yang sedang terbuka
  tabs: AppTabState[];

  // ID tab yang aktif
  activeTabId: string | null;

  // Actions
  openTab: (tab: AppTabDefinition) => void;
  closeTab: (tabId: string) => void;
  switchTab: (tabId: string) => void;
  getActiveTab: () => AppTabState | null;

  // Reset (untuk logout)
  reset: () => void;
}

// ============================================
// STORE
// ============================================

export const useAppTabsStore = create<AppTabsState>((set, get) => ({
  tabs: [],
  activeTabId: null,

  /**
   * Buka tab baru atau switch ke tab yang sudah ada
   * Jika tab dengan ID yang sama sudah terbuka, switch ke tab tersebut
   * Tab "beranda" selalu di posisi paling kiri
   */
  openTab: (tab) => {
    const { tabs } = get();

    // Cek apakah tab sudah ada
    const existingTab = tabs.find((t) => t.id === tab.id);
    if (existingTab) {
      // Tab sudah ada, switch ke tab tersebut
      set({ activeTabId: existingTab.id });
      return;
    }

    // Tab belum ada, buat tab baru
    const newTab: AppTabState = {
      id: tab.id,
      label: tab.label,
      path: tab.path,
      icon: tab.icon,
    };

    // Tambahkan tab baru
    let newTabs: AppTabState[];
    if (tab.id === 'beranda') {
      // Tab Beranda selalu di posisi pertama
      newTabs = [newTab, ...tabs];
    } else {
      // Tab lain ditambahkan di akhir
      newTabs = [...tabs, newTab];
    }

    set({
      tabs: newTabs,
      activeTabId: newTab.id,
    });
  },

  /**
   * Tutup tab
   * Jika tab yang ditutup adalah tab aktif, switch ke tab sebelumnya atau berikutnya
   */
  closeTab: (tabId) => {
    const { tabs, activeTabId } = get();

    // Cari index tab yang akan ditutup
    const tabIndex = tabs.findIndex((t) => t.id === tabId);
    if (tabIndex === -1) return;

    // Hapus tab
    const newTabs = tabs.filter((t) => t.id !== tabId);

    // Jika tab yang ditutup adalah tab aktif, tentukan tab baru yang aktif
    let newActiveTabId = activeTabId;
    if (tabId === activeTabId) {
      if (newTabs.length === 0) {
        // Tidak ada tab lagi
        newActiveTabId = null;
      } else if (tabIndex > 0) {
        // Switch ke tab sebelumnya
        newActiveTabId = newTabs[tabIndex - 1].id;
      } else {
        // Switch ke tab pertama
        newActiveTabId = newTabs[0].id;
      }
    }

    set({
      tabs: newTabs,
      activeTabId: newActiveTabId,
    });
  },

  /**
   * Switch ke tab tertentu
   */
  switchTab: (tabId) => {
    const { tabs } = get();
    const tab = tabs.find((t) => t.id === tabId);
    if (tab) {
      set({ activeTabId: tabId });
    }
  },

  /**
   * Get tab yang sedang aktif
   */
  getActiveTab: () => {
    const { tabs, activeTabId } = get();
    return tabs.find((t) => t.id === activeTabId) || null;
  },

  /**
   * Reset state (dipanggil saat logout)
   */
  reset: () => {
    set({
      tabs: [],
      activeTabId: null,
    });
  },
}));

// ============================================
// HELPER: Tab definitions dari menu
// ============================================

/**
 * Mapping path ke tab definition
 * Digunakan untuk membuat tab dari path menu sidebar
 */
export function createTabFromPath(path: string, label: string, icon?: ReactNode): AppTabDefinition {
  // Generate ID dari path (hapus leading slash dan ganti / dengan -)
  const id = path.replace(/^\//, '').replace(/\//g, '-') || 'beranda';

  return {
    id,
    label,
    path,
    icon,
  };
}
